import { useState } from "react";
import { Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EditModal } from "./EditModal";

export interface RawKeyEntry {
  key: string;
  permission?: string;
  lastUpdated?: string;
}

interface RawTabProps {
  keys: RawKeyEntry[];
  data: Record<string, string>;
  onSave: (key: string, value: string) => void;
}

function minify(value: string): string {
  if (!value) return "";
  try {
    return JSON.stringify(JSON.parse(value));
  } catch {
    return value;
  }
}

export function RawTab({ keys, data, onSave }: RawTabProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null);

  return (
    <div className="space-y-1">
      {keys.length === 0 && (
        <div className="text-sm text-muted-foreground px-3 py-6 text-center">
          No user data keys.
        </div>
      )}

      {keys.map((k) => {
        const display = minify(data[k.key] ?? "");
        return (
          <div
            key={k.key}
            className="flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-card"
          >
            <div className="flex items-center gap-2 shrink-0 w-72">
              <span className="font-mono text-xs text-foreground truncate" title={k.key}>
                {k.key}
              </span>
              {k.permission && (
                <Badge
                  variant={k.permission === "Public" ? "secondary" : "outline"}
                  className="text-[10px] shrink-0"
                >
                  {k.permission}
                </Badge>
              )}
            </div>
            <code
              className="text-xs text-muted-foreground flex-1 min-w-0 truncate font-mono"
              title={display}
            >
              {display || <span className="italic">empty</span>}
            </code>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingKey(k.key)}
              className="shrink-0 h-7 px-2 gap-1.5 text-xs"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </Button>
          </div>
        );
      })}

      {editingKey && (
        <EditModal
          keyName={editingKey}
          initialValue={data[editingKey] ?? ""}
          onSave={(val) => {
            onSave(editingKey, val);
            setEditingKey(null);
          }}
          onClose={() => setEditingKey(null)}
        />
      )}
    </div>
  );
}
